import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  SimpleGrid,
  Spinner,
  Stack,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { FiEdit2, FiInbox } from 'react-icons/fi';
import { UseQueryResult } from 'react-query';

import { SecretText } from '../../../../components/_components_0.2/BI/SecretField';
import { BiConfig, BiReport } from '../../../../services/biService';

interface Props {
  configs: UseQueryResult<BiConfig[]>;
  reports: BiReport[];
  onEdit: (config: BiConfig) => void;
}

// Rótulo em cima e valor embaixo: URLs e IDs longos quebram sem desalinhar.
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box minW="0">
      <Text fontWeight="medium" color="gray.650" fontSize="sm">
        {label}
      </Text>
      {children === null ||
      children === undefined ||
      typeof children === 'string' ? (
        <Text wordBreak="break-all" color={children ? undefined : 'gray.500'}>
          {children || '—'}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
}

// Mesmas informações do cartão antigo de configuração, em quatro colunas.
// Senha e client secret ficam escondidos, com o "olhinho" para mostrar.
export function ConfigsTab({ configs, reports, onEdit }: Props) {
  const usage = (configId: string) =>
    reports.filter(report => report.id_power_bi_configs === configId).length;

  if (configs.isLoading) {
    return (
      <Flex minH="160px" align="center" justify="center">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (configs.isError && !configs.data) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription flex="1">
          Não foi possível carregar as configurações.
        </AlertDescription>
        <Button size="sm" onClick={() => configs.refetch()}>
          Tentar novamente
        </Button>
      </Alert>
    );
  }

  if (!configs.data || configs.data.length === 0) {
    return (
      <Center py="10" flexDirection="column">
        <Icon as={FiInbox} fontSize="40" color="gray.500" />
        <Text color="gray.400" mt="3">
          Nenhuma configuração cadastrada
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      {configs.data.map(config => {
        const count = usage(config.id);

        return (
          <Box
            key={config.id}
            bg="gray.800"
            p="10px"
            borderRadius="6px"
            mb="10px"
          >
            <Flex
              justify="space-between"
              align="center"
              gridGap="3"
              wrap="wrap"
              px="2.5"
              pb="3"
            >
              <Flex align="center" gridGap="3" wrap="wrap" minW="0">
                <Text as="h2" fontSize="lg" wordBreak="break-all">
                  {config.pbiUsername}
                </Text>
                <Tooltip
                  bg="gray.650"
                  label="Relatórios que abrem com esta configuração"
                >
                  <Tag size="sm" colorScheme={count ? 'green' : 'gray'}>
                    {count === 1 ? '1 relatório' : `${count} relatórios`}
                  </Tag>
                </Tooltip>
              </Flex>

              <Tooltip bg="gray.650" label="Editar">
                <Button
                  aria-label={`Editar a configuração ${config.pbiUsername}`}
                  onClick={() => onEdit(config)}
                  type="button"
                  colorScheme="green"
                  color="white"
                >
                  <FiEdit2 />
                </Button>
              </Tooltip>
            </Flex>

            <SimpleGrid
              columns={{ base: 1, md: 2, xl: 4 }}
              spacingX="8"
              spacingY="4"
              px="2.5"
              pb="2"
            >
              <Stack spacing="3">
                <Field label="Authentication_mode:">
                  {config.authenticationMode}
                </Field>
                <Field label="Scope:">{config.scope}</Field>
                <Field label="Authority_uri:">{config.authorityUri}</Field>
              </Stack>
              <Stack spacing="3">
                <Field label="Api_url:">{config.apiUrl}</Field>
                <Field label="Client_id:">{config.clientId}</Field>
                <Field label="tenant_id:">{config.tenantId}</Field>
              </Stack>
              <Stack spacing="3">
                <Field label="Workspace_id:">{config.workspaceId}</Field>
                <Field label="Report_id:">{config.reportId}</Field>
                <Field label="pbi_username:">{config.pbiUsername}</Field>
              </Stack>
              <Stack spacing="3">
                <Field label="pbi_password:">
                  <SecretText value={config.pbiPassword} />
                </Field>
                <Field label="client_secret:">
                  <SecretText value={config.clientSecret} />
                </Field>
              </Stack>
            </SimpleGrid>
          </Box>
        );
      })}
    </Box>
  );
}
